import type { AcpSessionConfigOption } from './types';

type SelectOption = Extract<AcpSessionConfigOption, { type: 'select' }>;

/**
 * An ACP agent's own permission mode (its `mode` config option: Claude's Default, Accept edits,
 * Plan, Auto, Bypass). Crowdex's permissions are the only permission setting the user sees: the
 * agent stays in the mode where it asks before acting, so every action reaches Crowdex, which
 * answers it; only its plan mode, where it proposes a plan without acting, is offered, as "Plan
 * first".
 */
export type AgentModeChoices = {
  option: SelectOption;
  /** The mode where the agent asks before acting; Crowdex answers its requests. */
  ask: string | null;
  /** The mode where the agent proposes a plan without acting. */
  plan: string | null;
};

const ASK_MODE_VALUES = ['default'];

function values(option: SelectOption): string[] {
  return option.options.flatMap((entry) =>
    'options' in entry ? entry.options.map((choice) => choice.value) : [entry.value],
  );
}

export function agentModeChoices(
  options: readonly AcpSessionConfigOption[],
): AgentModeChoices | null {
  const option = options.find(
    (candidate): candidate is SelectOption =>
      candidate.type === 'select' && candidate.category === 'mode',
  );
  if (option === undefined) return null;
  const available = values(option);
  return {
    option,
    ask: ASK_MODE_VALUES.find((value) => available.includes(value)) ?? null,
    plan: available.find((value) => value === 'plan') ?? null,
  };
}

/** The mode the agent must be in: its plan mode when asked for and offered, else its asking mode. */
export function agentModeValue(choices: AgentModeChoices, requested: unknown): string | null {
  return requested === choices.plan && choices.plan !== null ? choices.plan : choices.ask;
}
